import { ImagePlus, LoaderCircle, RotateCw, X } from 'lucide-react';
import type { ReactElement } from 'react';
import type { UploadItem } from '@/shared/lib';

interface ImageUploadPickerProps {
  readonly items: readonly UploadItem[];
  readonly max: number;
  readonly onAdd: (files: readonly File[]) => void;
  readonly onRemove: (key: string) => void;
  readonly onRetry: (key: string) => void;
}

const TILE_CLASS = 'relative aspect-square overflow-hidden rounded-xl';

/** 정사각 타일 격자 — 올린 사진 뒤에 점선 "사진 추가" 타일을 두고, 개수(n/5)를 타일 안에 보여 준다. */
export const ImageUploadPicker = ({
  items,
  max,
  onAdd,
  onRemove,
  onRetry,
}: ImageUploadPickerProps): ReactElement => (
  <div className="flex flex-col gap-3">
    <ul className="grid grid-cols-3 gap-3 sm:grid-cols-5">
      {items.map((item, index) => (
        <li key={item.key} className={`${TILE_CLASS} bg-muted`}>
          <img
            src={item.previewUrl}
            alt={`올린 사진 ${index + 1}`}
            className="size-full object-cover"
          />
          {item.status === 'uploading' && (
            <span
              role="status"
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-background/75 text-xs font-medium"
            >
              <LoaderCircle aria-hidden className="size-4 animate-spin" />
              올리는 중
            </span>
          )}
          {item.status === 'failed' && (
            <button
              type="button"
              onClick={() => onRetry(item.key)}
              className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-background/85 text-xs font-medium text-destructive"
            >
              <RotateCw aria-hidden className="size-4" />
              다시 시도
            </button>
          )}
          <button
            type="button"
            aria-label={`사진 ${index + 1} 빼기`}
            onClick={() => onRemove(item.key)}
            className="absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full bg-foreground/70 text-background transition-colors hover:bg-foreground"
          >
            <X aria-hidden className="size-3.5" />
          </button>
        </li>
      ))}
      {items.length < max && (
        <li className={TILE_CLASS}>
          <label className="flex size-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-foreground/25 text-muted-foreground transition-colors hover:border-foreground hover:text-foreground has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-foreground">
            <ImagePlus aria-hidden className="size-5" />
            <span className="text-xs font-medium tabular-nums">
              {items.length} / {max}
            </span>
            <input
              type="file"
              aria-label="사진 추가"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="sr-only"
              onChange={(event) => {
                onAdd(Array.from(event.target.files ?? []));
                event.target.value = '';
              }}
            />
          </label>
        </li>
      )}
    </ul>
    <p className="text-xs text-muted-foreground">
      JPG·PNG·WEBP, 장당 10MB 이하, 최대 {max}장까지 올릴 수 있어요.
    </p>
  </div>
);
